type PurchaseIntentStage = 'package_page' | 'checkout';
type PurchaseIntentPlatform = 'web' | 'ios' | 'android';

type PurchaseIntentTrackPayload = {
  stage: PurchaseIntentStage;
  platform: PurchaseIntentPlatform;
  packageId?: string;
  appVersion?: string;
  metadata?: Record<string, unknown>;
};

type PurchaseIntentTrackResponse = {
  status: string;
  data?: {
    id: string;
    funnelStage: PurchaseIntentStage;
    packageId?: string;
    platform: PurchaseIntentPlatform;
    firstSeenAt: string;
    lastActivityAt: string;
    packagePageAt?: string;
    checkoutAt?: string;
    debounced?: boolean;
  };
};

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || '';
const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;
const STORAGE_PREFIX = 'purchase_intent_cooldown';

function getCooldownKey(userId: string, stage: PurchaseIntentStage): string {
  return `${STORAGE_PREFIX}:${userId}:${stage}`;
}

function canTrackNow(userId: string, stage: PurchaseIntentStage): boolean {
  if (typeof window === 'undefined') return false;
  const key = getCooldownKey(userId, stage);
  const lastTsRaw = localStorage.getItem(key);
  const lastTs = lastTsRaw ? Number(lastTsRaw) : 0;
  if (!lastTs || Number.isNaN(lastTs)) return true;
  return Date.now() - lastTs >= FOUR_HOURS_MS;
}

function markTrackedNow(userId: string, stage: PurchaseIntentStage): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(getCooldownKey(userId, stage), String(Date.now()));
}

async function trackPurchaseIntent(
  payload: PurchaseIntentTrackPayload,
  userId: string
): Promise<PurchaseIntentTrackResponse | null> {
  if (typeof window === 'undefined') return null;
  const token = localStorage.getItem('accessToken');
  if (!token || !API_BASE_URL) return null;

  const trimmedAppVersion = payload.appVersion?.slice(0, 64);
  const body: PurchaseIntentTrackPayload = {
    ...payload,
    appVersion: trimmedAppVersion,
  };

  // If packageId is unknown, omit it from payload.
  if (!body.packageId) {
    delete body.packageId;
  }

  const response = await fetch(`${API_BASE_URL}/purchase-intent/track`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    return null;
  }

  const result = (await response.json()) as PurchaseIntentTrackResponse;
  if (result?.status === 'success') {
    // Treat debounced=true as success (idempotent by backend contract).
    markTrackedNow(userId, payload.stage);
  }
  return result;
}

export const purchaseIntentService = {
  canTrackNow,
  trackPurchaseIntent,
};

